/**
 * @fileoverview Settings Store
 * @description Zustand store for system settings state management
 * @module @xtream/frontend/store/settings
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import {
  settingsApi,
  BrandingSettings,
  ThemeSettings,
  LoggingSettings,
  UpdateBrandingDTO,
  UpdateThemeDTO,
  UpdateLoggingDTO,
  DEFAULT_BRANDING,
  DEFAULT_THEME,
  DEFAULT_LOGGING,
} from '../services/api/settings.api';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface SettingsState {
  // State
  branding: BrandingSettings;
  theme: ThemeSettings;
  logging: LoggingSettings;
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;

  // Actions
  loadSettings: () => Promise<void>;
  updateBranding: (data: UpdateBrandingDTO) => Promise<void>;
  updateTheme: (data: UpdateThemeDTO) => Promise<void>;
  updateLogging: (data: UpdateLoggingDTO) => Promise<void>;
  resetToDefaults: () => void;
  clearError: () => void;
}

// ═══════════════════════════════════════════════════════════════════════════
// STORE
// ═══════════════════════════════════════════════════════════════════════════

export const useSettingsStore = create<SettingsState>()(
  devtools(
    persist(
      immer((set) => ({
        // Initial state
        branding: DEFAULT_BRANDING,
        theme: DEFAULT_THEME,
        logging: DEFAULT_LOGGING,
        isLoading: false,
        error: null,
        isInitialized: false,

        /**
         * Load all settings from the API
         */
        loadSettings: async () => {
          set((state) => {
            state.isLoading = true;
            state.error = null;
          });

          try {
            const settings = await settingsApi.getAll();
            const safeBranding = sanitizeBrandingSettings(settings.branding);

            set((state) => {
              state.branding = safeBranding;
              state.theme = settings.theme;
              state.logging = settings.logging;
              state.isLoading = false;
              state.isInitialized = true;
            });

            // Apply theme colors to CSS variables
            applyThemeColors(settings.theme.colors);

            // Apply branding (favicon + document title)
            applyBranding(safeBranding);
          } catch (error) {
            set((state) => {
              state.error = error instanceof Error ? error.message : 'Failed to load settings';
              state.isLoading = false;
              state.isInitialized = true;
            });
          }
        },

        /**
         * Update branding settings
         */
        updateBranding: async (data: UpdateBrandingDTO) => {
          set((state) => {
            state.isLoading = true;
            state.error = null;
          });

          try {
            const branding = await settingsApi.updateBranding(data);
            const safeBranding = sanitizeBrandingSettings(branding);

            set((state) => {
              state.branding = safeBranding;
              state.isLoading = false;
            });

            // Apply branding (favicon + document title)
            applyBranding(safeBranding);
          } catch (error) {
            set((state) => {
              state.error = error instanceof Error ? error.message : 'Failed to update branding';
              state.isLoading = false;
            });
            throw error;
          }
        },

        /**
         * Update theme settings
         */
        updateTheme: async (data: UpdateThemeDTO) => {
          set((state) => {
            state.isLoading = true;
            state.error = null;
          });

          try {
            const theme = await settingsApi.updateTheme(data);

            set((state) => {
              state.theme = theme;
              state.isLoading = false;
            });

            // Apply new theme colors to CSS variables
            applyThemeColors(theme.colors);
          } catch (error) {
            set((state) => {
              state.error = error instanceof Error ? error.message : 'Failed to update theme';
              state.isLoading = false;
            });
            throw error;
          }
        },

        /**
         * Update logging settings
         */
        updateLogging: async (data: UpdateLoggingDTO) => {
          set((state) => {
            state.isLoading = true;
            state.error = null;
          });

          try {
            const logging = await settingsApi.updateLogging(data);

            set((state) => {
              state.logging = logging;
              state.isLoading = false;
            });
          } catch (error) {
            set((state) => {
              state.error = error instanceof Error ? error.message : 'Failed to update logging';
              state.isLoading = false;
            });
            throw error;
          }
        },

        /**
         * Reset settings to defaults
         */
        resetToDefaults: () => {
          set((state) => {
            state.branding = DEFAULT_BRANDING;
            state.theme = DEFAULT_THEME;
            state.logging = DEFAULT_LOGGING;
          });
          applyThemeColors(DEFAULT_THEME.colors);
          applyBranding(DEFAULT_BRANDING);
        },

        /**
         * Clear error state
         */
        clearError: () => {
          set((state) => {
            state.error = null;
          });
        },
      })),
      {
        name: 'xtream-settings',
        partialize: (state) => ({
          branding: state.branding,
          theme: state.theme,
          logging: state.logging,
        }),
      },
    ),
    { name: 'SettingsStore' },
  ),
);

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Convert hex to HSL
 */
function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result || !result[1] || !result[2] || !result[3]) return { h: 0, s: 0, l: 0 };

  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

/**
 * Convert HSL to hex
 */
function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/**
 * Generate a color palette from a base color
 */
function generatePalette(baseHex: string): Record<string, string> {
  const { h, s } = hexToHsl(baseHex);

  return {
    '50': hslToHex(h, Math.min(s, 30), 97),
    '100': hslToHex(h, Math.min(s, 35), 94),
    '200': hslToHex(h, Math.min(s, 40), 86),
    '300': hslToHex(h, Math.min(s, 50), 72),
    '400': hslToHex(h, Math.min(s, 65), 56),
    '500': baseHex,
    '600': hslToHex(h, Math.min(s + 5, 100), 42),
    '700': hslToHex(h, Math.min(s + 10, 100), 35),
    '800': hslToHex(h, Math.min(s + 10, 100), 28),
    '900': hslToHex(h, Math.min(s + 10, 100), 22),
    '950': hslToHex(h, Math.min(s + 15, 100), 12),
  };
}

/**
 * Apply branding settings to the DOM
 * Updates favicon and document title dynamically
 */
function applyBranding(branding: BrandingSettings) {
  // Update document title
  if (branding.panelName) {
    document.title = branding.panelName;
  }

  // Update favicon
  const safeFaviconUrl = sanitizeBrandingUrl(branding.faviconUrl);
  if (safeFaviconUrl) {
    // Find existing favicon link or create a new one
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }

    // Determine the type based on the URL/data
    if (safeFaviconUrl.startsWith('data:')) {
      // Base64 data URL - extract MIME type from the data URL itself
      const mimeMatch = safeFaviconUrl.match(/^data:([^;]+);/);
      if (mimeMatch && mimeMatch[1]) {
        link.type = mimeMatch[1];
      }
    } else if (safeFaviconUrl.endsWith('.ico')) {
      link.type = 'image/x-icon';
    } else if (safeFaviconUrl.endsWith('.png')) {
      link.type = 'image/png';
    } else if (safeFaviconUrl.endsWith('.svg')) {
      link.type = 'image/svg+xml';
    }

    link.href = safeFaviconUrl;
  }
}

function sanitizeBrandingSettings(branding: BrandingSettings): BrandingSettings {
  return {
    ...branding,
    logoUrl: sanitizeBrandingUrl(branding.logoUrl) ?? null,
    faviconUrl: sanitizeBrandingUrl(branding.faviconUrl) ?? null,
  };
}

function sanitizeBrandingUrl(url?: string | null): string | undefined {
  if (!url) {
    return undefined;
  }

  const trimmed = url.trim();
  if (trimmed.length === 0) {
    return undefined;
  }

  if (trimmed.startsWith('data:')) {
    return isSafeDataImageUrl(trimmed) ? trimmed : undefined;
  }

  if (trimmed.startsWith('/')) {
    return trimmed;
  }

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
      return trimmed;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

function isSafeDataImageUrl(value: string): boolean {
  return /^data:image\/(png|jpeg|jpg|svg\+xml|x-icon|vnd\.microsoft\.icon|webp|gif);base64,/i.test(
    value,
  );
}

/**
 * Apply theme colors to CSS custom properties
 * This function updates all corporate colors AND generates derived palettes
 */
function applyThemeColors(colors: ThemeSettings['colors']) {
  const root = document.documentElement;

  // Extract colors with defaults to satisfy TypeScript
  const pink = colors['corporate-pink'] || '#ff2b76';
  const grayLight = colors['corporate-gray-light'] || '#21242a';
  const grayDark = colors['corporate-gray-dark'] || '#16181C';
  const white = colors['corporate-white'] || '#ffffff';

  // 1. Set the 4 base corporate colors
  root.style.setProperty('--color-corporate-pink', pink);
  root.style.setProperty('--color-corporate-gray-light', grayLight);
  root.style.setProperty('--color-corporate-gray-dark', grayDark);
  root.style.setProperty('--color-corporate-white', white);

  // 2. Generate and apply primary color palette from corporate-pink
  const primaryPalette = generatePalette(pink);
  Object.entries(primaryPalette).forEach(([shade, color]) => {
    root.style.setProperty(`--color-primary-${shade}`, color);
  });

  // 3. Update derived background colors
  root.style.setProperty('--bg-primary', grayDark);
  root.style.setProperty('--bg-secondary', grayLight);
  root.style.setProperty('--bg-elevated', grayLight);
  root.style.setProperty('--bg-card', grayLight);

  // 4. Update derived text colors
  root.style.setProperty('--text-primary', white);
  root.style.setProperty('--text-inverted', grayDark);

  // 5. Update sidebar and header colors
  root.style.setProperty('--sidebar-bg', grayDark);
  root.style.setProperty('--sidebar-text-active', white);
  root.style.setProperty('--header-bg', grayLight);

  // 6. Update focus ring to match primary color
  root.style.setProperty('--focus-ring', `0 0 0 3px ${pink}66`);

  // 7. Update info colors to match primary
  Object.entries(primaryPalette).forEach(([shade, color]) => {
    root.style.setProperty(`--color-info-${shade}`, color);
  });

  // 8. Generate hover variant of primary
  const { h, s, l } = hexToHsl(pink);
  root.style.setProperty('--color-corporate-pink-hover', hslToHex(h, s, Math.max(l - 8, 0)));
}

// Selector hooks for optimized re-renders
export const useBranding = () => useSettingsStore((state) => state.branding);
export const useTheme = () => useSettingsStore((state) => state.theme);
export const useLogging = () => useSettingsStore((state) => state.logging);
export const useSettingsLoading = () => useSettingsStore((state) => state.isLoading);
export const useSettingsError = () => useSettingsStore((state) => state.error);

export default useSettingsStore;
